import React, { useState, useEffect, useContext, useRef } from 'react'
import axios from 'axios'
import { API_URL } from '../../Constant';
import { toast } from 'react-toastify';
import messages from '../../assets/Messages.json'
import { useNavigate, useLocation } from 'react-router-dom';
import { showAppLoading, isNumeric, validateButtonASIBlock, getStatusFlagValue } from '../../Custom/Custom';
import { MyContextProvider } from "../../App";
import { jwtDecode } from 'jwt-decode';
import titlesData from '../../titles.json';
import asiTitle from '../../asi.json';
import StatusUnitDropdown from './StatusUnitDropdown';

// STATE_CODE: when null/undefined/NaN/empty/0 show and save "9"
function normalizeStateCode(val) {
   if (val == null || val === '' || String(val).trim() === '' || String(val) === '0') return '9';
   const n = Number(parseFloat(val));
   if (isNaN(n) || n === 0) return '9';
   return String(val).trim();
}

export default function BlockA({
   blockPageInfo,
   config,
   userInfoObject,
   blockInfo,
   accessAsiBlock,
   decode_token,
   previewMode = false
}) {
   const navigate = useNavigate();
   const location = useLocation();
   const previousLocationRef = useRef(location.pathname);
   const roleCodes = decode_token?.id?.ROLE_CODE
      ? String(decode_token.id.ROLE_CODE)
           .split('|')
           .map((r) => String(r).trim())
           .filter(Boolean)
      : [];
   const isDivasiRole = roleCodes.includes('DIVASI');
   const isHqRole = roleCodes.includes('HQADM') || roleCodes.includes('HQASI');
   
   const [buttonStatus, setButtonStatus] = useState('');
   const [year, setyear] = useState([]);
   const [unitCodes, setUnitCodes] = useState([]);
   const blockValidationRef = useRef({ isFetching: false, lastPayload: null });
   const getSurveyYearsRef = useRef({ isFetching: false, hasFetched: false });
   const getUnitCodesRef = useRef({ isFetching: false, hasFetched: false });
   const blockAFormRef = useRef({ isFetching: false, lastPayload: null });
   const blockAFormSaveToDBRef = useRef({ isFetching: false, lastPayload: null });
   const [formData, setFormData] = useState({
      dslNo: decode_token.id.USER_TYPE === 'FACTORY LEVEL' ? decode_token.id.USER_ID : '',
      surveyYear: '',
      pslNo: '',
      schemeCode: '',
      nicCodePerFrame: '',
      nicCodePerReturn: '',
      industryCodePerFrame: '',
      industryCodePerReturn: '',
      descriptionOfIndustry: '',
      stateCode: '',
      districtCode: '',
      sector: '',
      numberOfUnits: '',
      statusUnitCode: '',
      roSroCode: '',
      reason: '',
   });
   const isDivasiStatusFlagOne = isDivasiRole && String(formData?.statusFlag ?? '').trim() === '1';
   const isHqStatusFlagThree = isHqRole && String(formData?.statusFlag ?? '').trim() === '3';
   const isRestrictedRoleWithEmptyStatusFlag = (isDivasiRole || isHqRole) && String(formData?.statusFlag ?? '').trim() === '';
   const isNextBlockAlreadyAccessible = (() => {
      const ids = JSON.parse(localStorage.getItem('blockIds') || '[]');
      return Array.isArray(ids) && ids.map(Number).includes(2);
   })();

   const token = useContext(MyContextProvider).token;
   const userLevel = jwtDecode(token).id.USER_TYPE;

   const storedLanguage = localStorage.getItem('language');

   useEffect(() => {
      return () => {
         const currentPath = location.pathname;
         const blockRoutes = ['/BlockA', '/BlockB', '/BlockC', '/BlockD', '/BlockE', '/BlockF', '/BlockG', '/BlockH', '/BlockI', '/BlockJ', '/BlockK', '/BlockL', '/BlockM', '/BlockN'];
         const isGoingToABlock = blockRoutes.some(blockRoute => currentPath.includes(blockRoute));

         // Only clear if going to a non-Block route (like Dashboard)
         if (!currentPath.includes('/BlockA') && !isGoingToABlock) {
            localStorage.removeItem('blockInfo');
            localStorage.removeItem('statusUnitCode');
         }
      };
   }, [location.pathname]);

   // Reset form and clear blockInfo when coming from different route
   useEffect(() => {
      if (previewMode) {
         if (blockInfo && blockInfo.dslNo && blockInfo.surveyYear) {
            blockAFormRef.current = { isFetching: false, lastPayload: null };
            blockAFormSaveToDBRef.current = { isFetching: false, lastPayload: null };
            handleOnchangeSurveyYear({ dslNo: blockInfo.dslNo, surveyYear: blockInfo.surveyYear }, 'load');
         }
         getSurveyYears();
         getUnitCodes();
         return;
      }

      const currentPath = location.pathname;

      // Check if we're on BlockA route
      if (currentPath.includes('/BlockA')) {
         // Check if previous location was a different route (not BlockA)
         const previousPath = sessionStorage.getItem('previousRoute') || '';

         // List of Block routes (preserve data when coming from these)
         const blockRoutes = ['/BlockA', '/BlockB', '/BlockC', '/BlockD', '/BlockE', '/BlockF', '/BlockG', '/BlockH', '/BlockI', '/BlockJ', '/BlockK', '/BlockL', '/BlockM', '/BlockN'];
         const isPreviousRouteABlock = blockRoutes.some(blockRoute => previousPath.includes(blockRoute));

         if (previousPath && !previousPath.includes('/BlockA') && !isPreviousRouteABlock) {
            localStorage.removeItem('blockInfo');
            localStorage.removeItem('statusUnitCode');

            // Reset form data
            setFormData({
               dslNo: decode_token.id.USER_TYPE === 'FACTORY LEVEL' ? decode_token.id.USER_ID : '',
               surveyYear: '',
               pslNo: '',
               schemeCode: '',
               nicCodePerFrame: '',
               nicCodePerReturn: '',
               industryCodePerFrame: '',
               industryCodePerReturn: '',
               descriptionOfIndustry: '',
               stateCode: '',
               districtCode: '',
               sector: '',
               numberOfUnits: '',
               statusUnitCode: '',
               roSroCode: '',
               reason: '',
            });

            // Reset refs to allow fresh API calls
            blockAFormRef.current = { isFetching: false, lastPayload: null };
            blockAFormSaveToDBRef.current = { isFetching: false, lastPayload: null };

            // Don't make API call, just show empty form
            localStorage.setItem('blockIds', JSON.stringify([1]));
            accessAsiBlock([1]);
            getSurveyYears();
            getUnitCodes();
            // Store current route for next time
            sessionStorage.setItem('previousRoute', currentPath);
            return;
         }

         // Store current route for next time
         sessionStorage.setItem('previousRoute', currentPath);

         // Normal flow - check if blockInfo exists (preserve data when coming from other blocks)
         const storedBlockInfo = JSON.parse(localStorage.getItem('blockInfo')) || { dslNo: '', surveyYear: '' };

         // If coming from another Block, always make API call if blockInfo exists
         if (isPreviousRouteABlock && previousPath && !previousPath.includes('/BlockA')) {
            // Reset refs to force fresh API call
            blockAFormRef.current = { isFetching: false, lastPayload: null };
            blockAFormSaveToDBRef.current = { isFetching: false, lastPayload: null };

            // Set flag to force API call when coming from another Block
            sessionStorage.setItem('forceBlockAAPI', 'true');

            // Force API call when coming from another Block if blockInfo exists
            if (storedBlockInfo && storedBlockInfo.dslNo && storedBlockInfo.surveyYear) {
               // Ensure values are strings/numbers
               const blockInfoToLoad = {
                  dslNo: String(storedBlockInfo.dslNo).trim(),
                  surveyYear: storedBlockInfo.surveyYear
               };

               // Call API immediately - flag will bypass duplicate check
               handleOnchangeSurveyYear(blockInfoToLoad, 'load');
            }

            getSurveyYears();
            getUnitCodes();
            // Store current route for next time
            sessionStorage.setItem('previousRoute', currentPath);
            return;
         }

         // Normal flow when not coming from another Block
         // Always check if blockInfo exists and make API call if dslNo and surveyYear are present
         if (storedBlockInfo && storedBlockInfo.dslNo && storedBlockInfo.surveyYear) {
            // Reset refs to ensure fresh API call
            blockAFormRef.current = { isFetching: false, lastPayload: null };
            // Make API call to fetch data
            handleOnchangeSurveyYear(storedBlockInfo, 'load');
         } else {
            // If no blockInfo, show empty form
            if (userLevel === 'FACTORY LEVEL') {
               // Factory level - DSL No is pre-filled
               localStorage.setItem('blockIds', JSON.stringify([1]));
               accessAsiBlock([1]);
            } else {
               // Other levels - show empty form
               localStorage.setItem('blockIds', JSON.stringify([1]));
               accessAsiBlock([1]);
            }
         }
         getSurveyYears();
         getUnitCodes();
      } else {
         // Store current route when not on BlockA
         sessionStorage.setItem('previousRoute', currentPath);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [location.pathname, previewMode, blockInfo?.dslNo, blockInfo?.surveyYear])


   const getSurveyYears = async () => {
      // Prevent multiple calls
      if (getSurveyYearsRef.current.isFetching || getSurveyYearsRef.current.hasFetched) {
         return;
      }

      getSurveyYearsRef.current.isFetching = true;
      showAppLoading(true)
      try {
         const response = await axios.get(`${API_URL}api/asi/surveyYearList`, config);
         const allYear = response.data;

         if (allYear.status) {
            if(userLevel==='FACTORY LEVEL'){
               setyear([2024,2023])
            }else{
               setyear(allYear.data.surveyYearList)
            }
          
            getSurveyYearsRef.current.hasFetched = true;
            showAppLoading(false);
         }
      } catch (error) {
         console.error(error);
         showAppLoading(false);
      } finally {
         showAppLoading(false);
         getSurveyYearsRef.current.isFetching = false;
      }

   }

   const getUnitCodes = async () => {
      // Prevent multiple calls
      if (getUnitCodesRef.current.isFetching || getUnitCodesRef.current.hasFetched) {
         return;
      }

      getUnitCodesRef.current.isFetching = true;
      try {
         const response = await axios.get(`${API_URL}api/asi/unitCodes`, config);
         if (response.data.status) {
            setUnitCodes(response.data.data || []);
            getUnitCodesRef.current.hasFetched = true;
         }
      } catch (error) {
         console.error('Error fetching unit codes:', error);
         setUnitCodes([]);
      } finally {
         getUnitCodesRef.current.isFetching = false;
      }
   }

   // Function to validate Description of Industry field
   const validateDescriptionOfIndustry = (value) => {
      if (!value) return '';

      // Standard text field validation: allow alphanumeric, spaces, and common punctuation
      // Remove only potentially harmful characters (like script tags, but allow normal text)
      // Max length: 200 characters (industry standard for description fields)
      let sanitized = value;

      // Limit to 200 characters
      if (sanitized.length > 200) {
         sanitized = sanitized.substring(0, 200);
      }

      return sanitized;
   };

   // Function to handle State on change
   const handleChange = (e) => {
      const { name, value } = e.target;

      if (name === "dslNo") {
         if (!isNumeric(value)) {
            handleReset();
            return false;
         }
         // If DSL is cleared, reset saved/access blocks so progress bar turns grey again
         if (String(value).trim() === '') {
            localStorage.removeItem('blockInfo');
            localStorage.removeItem('statusUnitCode');
            localStorage.setItem('blockIds', JSON.stringify([1]));
            accessAsiBlock([1]);
         }
      }
      

      if (name === 'statusUnitCode') {
         if (value === '0') {
            return false;
         }
      }


      // Check if DSL No. is empty before allowing survey year selection
      if (name === 'surveyYear') {
         const dslNoValue = formData.dslNo ? String(formData.dslNo).trim() : '';
         if (!dslNoValue || dslNoValue === '') {
            toast.error('Please Enter DSL Number', messages.COLOR_RED);
            // Don't update surveyYear if DSL is empty
            return false;
         }
      }

      setFormData({ ...formData, [name]: value });


      if (name === 'surveyYear') {
         // Only call API if DSL is not empty
         const dslNoValue = formData.dslNo ? String(formData.dslNo).trim() : '';
         if (dslNoValue && dslNoValue !== '') {
            handleOnchangeSurveyYear(value, 'onChange')
         }
      }

      // If DSL is entered (6 digits) and survey year is already selected, automatically call API
      if (name === 'dslNo') {
         const dslValue = value ? String(value).trim() : '';
         if (dslValue && dslValue !== '' && dslValue.length === 6 && formData.surveyYear) {
            // Small delay to ensure formData is updated
            setTimeout(() => {
               handleOnchangeSurveyYear(formData.surveyYear, 'onChange');
            }, 100);
         }
      }

      // Handle Industry Code Per Return API call
      if (name === 'industryCodePerReturn' || name === 'nicCodePerReturn') {
         const codeValue = value ? String(value).trim() : '';
         // Only call API if value is 5 digits (complete code)
         if (codeValue && codeValue.length === 5) {
            fetchNicIndustryDescription(codeValue);
         } else if (codeValue === '') {
            // If field is cleared, also clear description
            setFormData(prev => ({ ...prev, descriptionOfIndustry: '' }));
         }
      }

      if (name === 'statusUnitCode') {

         let statusUnitCode = parseInt(value);
         if (statusUnitCode === 1) {
            toast.success(messages.STATUS_UNITE_FIRST_MSG, messages.COLOR_RED);
         } else if (statusUnitCode === 2) {
            toast.success(messages.STATUS_UNITE_SECOND_MSG, messages.COLOR_RED);
         } else if (statusUnitCode === 3) {
            toast.success(messages.STATUS_UNITE_THIRD_MSG, messages.COLOR_RED);
         } else if (statusUnitCode === 6) {
            toast.success(messages.STATUS_UNITE_FOURTH_MSG, messages.COLOR_RED);
         } else {
            toast.success(messages.STATUS_UNITE_FIFTH_MSG, messages.COLOR_RED);
         }

      }


   };

   // Function to fetch NIC Industry Description
   const fetchNicIndustryDescription = async (nicCode) => {
      if (!nicCode || nicCode.length !== 5) {
         return;
      }

      try {
         showAppLoading(true);
         const response = await axios.get(
            `${API_URL}api/asi/nicIndustryMaster?niccode=${nicCode}`,
            config
         );
         // const response = await axios.get(
         //    `https://updesdev.srmtechsol.com/api/asi/nicIndustryMaster?niccode=${nicCode}`,
         //    config
         // );


         if (response.data && response.data.status && response.data.data && response.data.data.length > 0) {
            const nicDescription = response.data.data[0].nicDescription || '';
            // Update description of industry field
            setFormData(prev => ({
               ...prev,
               descriptionOfIndustry: nicDescription
            }));
         } else {
            // If status is false, show error message from response
            if (response.data && response.data.status === false && response.data.message) {
               toast.error(response.data.message, messages.COLOR_RED);
            }
            // If no data found, clear description
            setFormData(prev => ({
               ...prev,
               descriptionOfIndustry: ''
            }));
         }
      } catch (error) {
         console.error('Error fetching NIC Industry Description:', error);
         setFormData(prev => ({
            ...prev,
            descriptionOfIndustry: ''
         }));
         toast.error('NIC Industry code not found', messages.COLOR_RED);
         // Don't show error toast, just log it
      } finally {
         showAppLoading(false);
      }
   };

   //All block validation Data
   const blockValidation = async (postData) => {
      // Create a unique key for the payload
      const payloadKey = JSON.stringify(postData);

      // Check if same payload is already being fetched
      if (blockValidationRef.current.isFetching && blockValidationRef.current.lastPayload === payloadKey) {
         return;
      }

      // Check if same payload was just fetched
      if (blockValidationRef.current.lastPayload === payloadKey && !blockValidationRef.current.isFetching) {
         return;
      }

      try {
         blockValidationRef.current.isFetching = true;
         blockValidationRef.current.lastPayload = payloadKey;
         showAppLoading(true);
         const response = await axios.post(`${API_URL}api/asi/blockValidation`, postData, config);
         const { data, status } = response.data;
         const accessIds = data.allblockData
         if (accessIds.length > 0) {
            accessAsiBlock(accessIds)
         } else {
            localStorage.setItem('blockIds', JSON.stringify([1]));
            accessAsiBlock([1])
         }

         return true;

      } catch (error) {
         console.error(error);
         toast.error('An error occurred while processing your request.', messages.COLOR_RED);
      } finally {
         showAppLoading(false);
         blockValidationRef.current.isFetching = false;
      }

   }

   // Function to handle survey year on change data

   const handleOnchangeSurveyYear = async (value, type) => {

      let postData = {};
      if (type === 'onChange') {
         postData = {
            dsl_no: formData.dslNo,
            survey_year: value
         }
      } else if (type === "load") {
         postData = {
            dsl_no: value.dslNo,
            survey_year: value.surveyYear
         }
      }


      if (decode_token.id.USER_TYPE === 'FACTORY LEVEL' && parseInt(postData.dsl_no) !== parseInt(decode_token.id.USER_ID)) {
         setFormData({
            dslNo: decode_token.id.USER_TYPE === 'FACTORY LEVEL' ? decode_token.id.USER_ID : '',
            surveyYear: '',
            pslNo: '',
            schemeCode: '',
            nicCodePerFrame: '',
            nicCodePerReturn: '',
            industryCodePerFrame: '',
            industryCodePerReturn: '',
            descriptionOfIndustry: '',
            stateCode: '',
            districtCode: '',
            sector: '',
            numberOfUnits: '',
            statusUnitCode: '',
            roSroCode: '',
            reason: '',
         })
         toast.error("DSL No is not Valid.", messages.COLOR_RED);
         return false;
      }

      // Create a unique key for the payload
      const payloadKey = JSON.stringify(postData);

      // Check if coming from another Block - if so, force API call by checking a flag
      const isComingFromAnotherBlock = sessionStorage.getItem('forceBlockAAPI') === 'true';
      if (isComingFromAnotherBlock) {
         // Clear the flag
         sessionStorage.removeItem('forceBlockAAPI');
         // Reset refs to ensure fresh call - this bypasses duplicate check
         blockAFormRef.current = { isFetching: false, lastPayload: null };
         // Force API call - skip duplicate checks
      } else {
         // Normal duplicate check only if not coming from another Block
         // Check if same payload is already being fetched
         if (blockAFormRef.current.isFetching && blockAFormRef.current.lastPayload === payloadKey) {
            return;
         }

         // Check if same payload was just fetched
         if (blockAFormRef.current.lastPayload === payloadKey && !blockAFormRef.current.isFetching) {
            return;
         }
      }

      blockAFormRef.current.isFetching = true;
      blockAFormRef.current.lastPayload = payloadKey;
      showAppLoading(true);
      try {
         const response = await axios.post(`${API_URL}api/asi/blockAForm`, postData, config);
         const { data, status, message } = response.data;
         if (status) {
            const blockData = data.blockAData;

            const objToStore = {
               dslNo: blockData.dslNo,
               surveyYear: blockData.surveyYear,
            };
            localStorage.setItem('blockInfo', JSON.stringify(objToStore));
            localStorage.setItem('statusUnitCode', blockData.statusOfUnitCode)

            const BlockDataObject = {
               dslNo: blockData.dslNo,
               surveyYear: blockData.surveyYear,
               pslNo: blockData.pslNo,
               schemeCode: blockData.schemeCode,

               nicCodePerFrame: blockData.nicCodePerFrame || blockData.industryCodePerFrame || '',
               nicCodePerReturn: blockData.nicCodePerReturn || blockData.industryCodePerReturn || '',


                industryCodePerFrame: blockData.industryCodePerFrame || blockData.nicCodePerFrame || '',
                industryCodePerReturn: blockData.nicCodePerReturn  || '',//|| blockData.industryCodePerReturn ||

               descriptionOfIndustry: blockData.descriptionOfIndustry || blockData.DescriptionofIndustry || blockData.DescriptionOfIndustry || '',
               stateCode: normalizeStateCode(blockData.stateCode ?? blockData.state_code ?? ''),
               districtCode: blockData.districtCode,
               sector: blockData.sector,
               numberOfUnits: blockData.numberOfUnits,
               statusUnitCode: blockData.statusOfUnitCode,
               roSroCode: blockData.roSroCode,
               statusFlag: blockData.statusFlag,
               reason: blockData.reason

            }
            // console.log("BlockDataObject", BlockDataObject)
            setFormData(BlockDataObject)

            const statusFlage = blockData.statusFlag;
            const buttonStatus = await validateButtonASIBlock(decode_token, statusFlage);
            setButtonStatus(buttonStatus);
            blockValidation(postData)

         } else {

            const blockData = data.payload;  //payload
            toast.error(message, messages.COLOR_RED);
            setFormData({
               dslNo: blockData.dsl_no,
               surveyYear: blockData.survey_year,
               pslNo: '',
               schemeCode: '',
               nicCodePerFrame: '',
               nicCodePerReturn: '',
               industryCodePerFrame: '',
               industryCodePerReturn: '',
               descriptionOfIndustry: '',
               stateCode: '',
               districtCode: '',
               sector: '',
               numberOfUnits: '',
               statusUnitCode: '',
               roSroCode: '',
               reason: '',
            })
            localStorage.setItem('blockIds', JSON.stringify([1]));
            accessAsiBlock([1])
         }
      } catch (error) {
         console.error(error);
         toast.error('An error occurred while processing your request.', messages.COLOR_RED);
      } finally {
         setTimeout(() => {
            showAppLoading(false);
            blockAFormRef.current.isFetching = false;
         }, 800);
      }
   }

   // Function to handle reset Form Data
   const handleReset = () => {
      setFormData({
         dslNo: '',
         surveyYear: '',
         pslNo: '',
         schemeCode: '',
         nicCodePerFrame: '',
         nicCodePerReturn: '',
         industryCodePerFrame: '',
         industryCodePerReturn: '',
         descriptionOfIndustry: '',
         stateCode: '',
         districtCode: '',
         sector: '',
         numberOfUnits: '',
         statusUnitCode: '',
         roSroCode: '',
         reason: '',
      })
      // Also reset stored context so blocks don't remain green
      localStorage.removeItem('blockInfo');
      localStorage.removeItem('statusUnitCode');
      localStorage.setItem('blockIds', JSON.stringify([1]));
      accessAsiBlock([1]);

   }

   // Function to handle validate DSL text box filed
   const validateDSL = (dslNumber) => {

      let dslNo = dslNumber.toString();
      if (dslNo?.length === 0) {
         toast.error(messages.DSL_MAND_MSG, messages.COLOR_RED);
         return false;
      } else if (dslNo.length !== 6) {
         toast.error(messages.DSL_SIX_MSG, messages.COLOR_RED);
         return false;
      } else if (decode_token.id.USER_TYPE === 'FACTORY LEVEL' && dslNo !== decode_token.id.USER_ID) {
         toast.error('You are not an authorized user to capture the data for this DSL', messages.COLOR_RED);
         return false;
      } else {
         return true;
      }
   }


   // Function to handle validate umber Of Units text box filed
   function validateNumberOfUnits(val) {
      if (val > 9) {
         toast.error(messages.UNIT_MORE_NINE_MSG, messages.COLOR_RED);
         return false;
      } else if (val > 1) {
         toast.error(messages.DSL_NUMBER_ONE_MSG, messages.COLOR_RED);
         return false;
      }
      return true;
   }


   // Function to handle validate whole form data
   const validateForm = () => {
      if (!validateDSL(formData.dslNo)) {
         console.log("indide validateform")
         return false;
      }

      // Default to 2024-25 format if survey year is empty or 2024
      const is2024Year = !formData.surveyYear || formData.surveyYear === 2024 || formData.surveyYear === '2024';

      // Check if Status of Unit Code requires Industry Code Per Return to be mandatory
      // Industry Code Per Return is mandatory only for Status Unit Code 1, 2, 3
      // For Status Unit Code 4, 5, 7, 8 it is optional
      // Convert to string for consistent comparison
      const statusUnitCodeStr = String(formData.statusUnitCode || '').trim();
      const isStatusUnitCodeRequiresIndustryCode = statusUnitCodeStr === '1' || statusUnitCodeStr === '2' || statusUnitCodeStr === '3';

      let mandatoryFields = [];

      if (is2024Year) {
         // For 2024-25, use new field names
         mandatoryFields = [
            { field: 'surveyYear', message: messages.SURVEY_MANDATORY_MSG },
            { field: 'pslNo', message: messages.PSL_MANDATORY_MSG },
            { field: 'schemeCode', message: messages.SCHEME_MANDATORY_MSG },
            { field: 'industryCodePerFrame', message: messages.NIC_CODE_FRAME_MANDATORY_MSG },
            // Industry Code Per Return is mandatory only if Status of Unit Code is 1, 2, or 3
            // For Status Unit Code 4, 5, 7, 8 it is optional
            ...(isStatusUnitCodeRequiresIndustryCode ? [{ field: 'industryCodePerReturn', message: messages.NIC_CODE_RETURN_MANDATORY_MSG }] : []),
            { field: 'stateCode', message: 'State Code is mandatory' },
            { field: 'districtCode', message: messages.DISTRICT_MANDATORY_MSG },
            { field: 'sector', message: messages.SECTOR_MANDATORY_MSG },
            { field: 'numberOfUnits', message: messages.NOOFUNIT_MANDATORY_MSG },
            { field: 'statusUnitCode', message: messages.STATUSOFUNIT_MANDATORY_MSG },
            // Description of Industry (NIC Industry code not found) is mandatory only if Status of Unit Code is 1, 2, or 3
            ...(isStatusUnitCodeRequiresIndustryCode ? [{ field: 'descriptionOfIndustry', message: 'NIC Industry code not found' }] : []),
         ];
      } else {
         // For other years, use old field names
         mandatoryFields = [
            { field: 'surveyYear', message: messages.SURVEY_MANDATORY_MSG },
            { field: 'pslNo', message: messages.PSL_MANDATORY_MSG },
            { field: 'schemeCode', message: messages.SCHEME_MANDATORY_MSG },
            { field: 'nicCodePerFrame', message: messages.NIC_CODE_FRAME_MANDATORY_MSG },
            // NIC Code Per Return is mandatory only if Status of Unit Code is 1, 2, or 3
            // For Status Unit Code 4, 5, 7, 8 it is optional
            ...(isStatusUnitCodeRequiresIndustryCode ? [{ field: 'nicCodePerReturn', message: messages.NIC_CODE_RETURN_MANDATORY_MSG }] : []),
            { field: 'districtCode', message: messages.DISTRICT_MANDATORY_MSG },
            { field: 'sector', message: messages.SECTOR_MANDATORY_MSG },
            { field: 'numberOfUnits', message: messages.NOOFUNIT_MANDATORY_MSG },
            { field: 'statusUnitCode', message: messages.STATUSOFUNIT_MANDATORY_MSG },
            // Description of Industry (NIC Industry code not found) is mandatory only if Status of Unit Code is 1, 2, or 3
            ...(isStatusUnitCodeRequiresIndustryCode ? [{ field: 'descriptionOfIndustry', message: 'NIC Industry code not found' }] : []),
         ];
      }

      console.log("mandatoryFields", mandatoryFields)
      console.log("statusUnitCode:", formData.statusUnitCode, "isStatusUnitCodeRequiresIndustryCode:", isStatusUnitCodeRequiresIndustryCode)
      
      for (const { field, message } of mandatoryFields) {
         const value = field === 'stateCode' ? normalizeStateCode(formData[field]) : formData[field];

         // Only validate Industry Code Per Return if Status of Unit Code is 1, 2, or 3 (mandatory)
         // For Status Unit Code 4, 5, 7, 8 it is optional, so skip validation
         if ((field === 'nicCodePerReturn' || field === 'industryCodePerReturn') && isStatusUnitCodeRequiresIndustryCode) {

            if (!/^\d*$/.test(value)) {
               toast.error('Code Per Return should be numeric.', messages.COLOR_RED);
               return false;
            }

            if (!/^\d{5}$/.test(value) && value !== '') {
               toast.error("Enter valid Industry Code Per Return", messages.COLOR_RED);
               return false
            }

         }

         if (!value) {
            toast.error(message, messages.COLOR_RED);
            return false;
         }
      }


      if (![1, 2, 3, 4, 5, 7, 8, '', null].includes(parseInt(formData.statusUnitCode))) {
         toast.error(messages.STATUS_UNITE_MSG, messages.COLOR_RED);
         return false;
      }

      if (!validateNumberOfUnits(formData.numberOfUnits)) {
         return false;
      }
      return true;

   }

   // Function to handle going to the next block
   const handleNext = () => {
      // Next unlocked + Save disabled → skip save, sirf navigate (DIVASI/FACTO/DISASI/HQ sab)
      if (isNextBlockAlreadyAccessible && (buttonStatus || isDivasiStatusFlagOne || isHqStatusFlagThree || isRestrictedRoleWithEmptyStatusFlag)) {
         navigate("/BlockB");
         return;
      }
      // DIVASI + statusFlag=1: do not save, just navigate
      if (isDivasiStatusFlagOne) {
         navigate("/BlockB");
         return;
      }
      // HQADM/HQASI + statusFlag=3: do not save, just navigate
      if (isHqStatusFlagThree) {
         navigate("/BlockB");
         return;
      }
      const confirmed = window.confirm("Have you saved your changes?");
      if (!confirmed) return;
      if (!validateForm()) {
         return false;
      }
      console.log("indide handlenext")
      handleSaveData('', 'SAVE')
      navigate("/BlockB")
   }


   // Function to handle update and save form data
   const handleSaveData = async (e, buttonType) => {

      if (!validateForm()) {
         return false;
      }

      const clickButtonStatusFlag = getStatusFlagValue(buttonType, userInfoObject.userLevel);
      // Default to 2024-25 format if survey year is empty or 2024
      const is2024Year = !formData.surveyYear || formData.surveyYear === 2024 || formData.surveyYear === '2024';

      // Prepare data object based on survey year
      let dataToSend = { ...formData };
      dataToSend.stateCode = normalizeStateCode(formData.stateCode);

      // Format districtCode: if single digit, pad with leading zero (1 -> 01, 2 -> 02)
      if (dataToSend.districtCode) {
         const districtCodeStr = String(dataToSend.districtCode).trim();
         // If it's a single digit number, pad with leading zero
         if (/^\d$/.test(districtCodeStr)) {
            dataToSend.districtCode = districtCodeStr.padStart(2, '0');
         }
         // If already double digit or has leading zero, keep as is
      }

      if (is2024Year) {
         // For 2024-25, send nicCodePerFrame and nicCodePerReturn in payload (not industryCodePerFrame/industryCodePerReturn)
         dataToSend.nicCodePerFrame = formData.industryCodePerFrame || formData.nicCodePerFrame;
         dataToSend.nicCodePerReturn = formData.industryCodePerReturn || formData.nicCodePerReturn;
         // Remove industryCodePerFrame and industryCodePerReturn from payload for 2024-25
         delete dataToSend.industryCodePerFrame;
         delete dataToSend.industryCodePerReturn;
      } else {
         // For other years, keep nic code fields only
         dataToSend.nicCodePerFrame = formData.nicCodePerFrame || formData.industryCodePerFrame;
         dataToSend.nicCodePerReturn = formData.nicCodePerReturn || formData.industryCodePerReturn;
         // Remove industryCodePerFrame and industryCodePerReturn from payload for other years
         delete dataToSend.industryCodePerFrame;
         delete dataToSend.industryCodePerReturn;
      }

      const { statusUnitCode, ...modifiedObject } = {
         ...dataToSend,
         statusOfUnitCode: formData.statusUnitCode,
         statusFlag: clickButtonStatusFlag
      };

      const payload = {
         dsl_no: formData.dslNo,
         survey_year: formData.surveyYear,
         buttonType: buttonType
      }
      const newObject = {
         itemCodes: modifiedObject,
         payload: payload
      }

      // Create a unique key for the payload
      const payloadKey = JSON.stringify(newObject);

      // Check if same payload is already being fetched
      if (blockAFormSaveToDBRef.current.isFetching && blockAFormSaveToDBRef.current.lastPayload === payloadKey) {
         return;
      }

      // Check if same payload was just fetched
      if (blockAFormSaveToDBRef.current.lastPayload === payloadKey && !blockAFormSaveToDBRef.current.isFetching) {
         return;
      }

      blockAFormSaveToDBRef.current.isFetching = true;
      blockAFormSaveToDBRef.current.lastPayload = payloadKey;
      showAppLoading(true);

      try {
         const response = await axios.post(`${API_URL}api/asi/blockAFormSaveToDB`, newObject, config);
         const { status, message } = response.data;
         if (status) {
            // accessAsiBlock([1,2])
            toast.success(message, messages.COLOR_RED);
            localStorage.setItem('statusUnitCode', formData.statusUnitCode)
            blockValidation(payload)
         }
      } catch (error) {
         console.error(error);
         toast.error('An error occurred while processing your request.', messages.COLOR_RED);
      } finally {
         showAppLoading(false);
         blockAFormSaveToDBRef.current.isFetching = false;
      }

   };



   return (
      <div className="card-body">
         {/* <ToastContainer/> */}
         {/* <form  onSubmit={handleSubmit}> */}
         <h4 className="page_heading border-bottom-0">
            <div dangerouslySetInnerHTML={{ __html: storedLanguage === 'hi' ? asiTitle.hi.BLOCK_A_TITLE : asiTitle.en.BLOCK_A_TITLE }} />
         </h4>


         <div className="row">
            {(() => {
               // Default to 2024-25 format if survey year is empty or 2024
               const is2024Year = !formData.surveyYear || formData.surveyYear === 2024 || formData.surveyYear === '2024';

               if (is2024Year) {
                  // For 2024-25: Survey Year first (no serial), DSL No. second (serial 1)
                  return (
                     <>
                        <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                           <label htmlFor="dslNo"> 1. {storedLanguage === 'hi' ? 'अनुसूची प्रेषण (DSL) संख्या' : 'Schedule Despatch (DSL) No.'}</label>
                           <input
                              type="text"
                              className="form-control"
                              id="dslNo"
                              name="dslNo"
                              maxLength="6"
                              value={formData.dslNo}
                              onChange={handleChange}
                              onBlur={(e) => {
                                 validateDSL(e.target.value);
                                 // If DSL is valid and survey year is selected, automatically call API
                                 const dslValue = e.target.value ? String(e.target.value).trim() : '';
                                 if (dslValue && dslValue !== '' && dslValue.length === 6 && formData.surveyYear) {
                                    handleOnchangeSurveyYear(formData.surveyYear, 'onChange');
                                 }
                              }}

                           />
                        </div>

                        <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                           <label htmlFor="surveyYear"> {storedLanguage === 'hi' ? asiTitle.hi.SERVEY_YEAR : asiTitle.en.SERVEY_YEAR}</label>
                           <select
                              id="surveyYear"
                              name="surveyYear"
                              className="form-select"
                              value={formData.surveyYear}
                              onChange={handleChange}
                           >
                              <option value="">Select</option>
                              {year.map((yearName, index) => (
                                 <option key={index} value={yearName}>
                                    {yearName}-{(yearName + 1).toString().slice(2)}
                                 </option>
                              ))}
                           </select>
                        </div>

                        {/* <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                           <label htmlFor="dslNo"> 1. {storedLanguage === 'hi' ? 'अनुसूची प्रेषण (DSL) संख्या' : 'Schedule Despatch (DSL) No.'}</label>
                           <input
                              type="text"
                              className="form-control"
                              id="dslNo"
                              name="dslNo"
                              maxLength="6"
                              value={formData.dslNo}
                              onChange={handleChange}
                              onBlur={(e) => {
                                 validateDSL(e.target.value);
                                 // If DSL is valid and survey year is selected, automatically call API
                                 const dslValue = e.target.value ? String(e.target.value).trim() : '';
                                 if (dslValue && dslValue !== '' && dslValue.length === 6 && formData.surveyYear) {
                                    handleOnchangeSurveyYear(formData.surveyYear, 'onChange');
                                 }
                              }}

                           />
                        </div> */}
                     </>
                  );
               } else {
                  // For other years: DSL No. first (serial 1), Survey Year second (serial 2)
                  return (
                     <>
                        <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                           <label htmlFor="dslNo"> 1. {storedLanguage === 'hi' ? asiTitle.hi.DSL_NO : asiTitle.en.DSL_NO}</label>
                           <input
                              type="text"
                              className="form-control"
                              id="dslNo"
                              name="dslNo"
                              maxLength="6"
                              value={formData.dslNo}
                              onChange={handleChange}
                              onBlur={(e) => {
                                 validateDSL(e.target.value);
                                 // If DSL is valid and survey year is selected, automatically call API
                                 const dslValue = e.target.value ? String(e.target.value).trim() : '';
                                 if (dslValue && dslValue !== '' && dslValue.length === 6 && formData.surveyYear) {
                                    handleOnchangeSurveyYear(formData.surveyYear, 'onChange');
                                 }
                              }}

                           />
                        </div>

                        <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                           <label htmlFor="surveyYear"> 2. {storedLanguage === 'hi' ? asiTitle.hi.SERVEY_YEAR : asiTitle.en.SERVEY_YEAR}</label>
                           <select
                              id="surveyYear"
                              name="surveyYear"
                              className="form-select"
                              value={formData.surveyYear}
                              onChange={handleChange}
                           >
                              <option value="">Select</option>
                              {year.map((yearName, index) => (
                                 <option key={index} value={yearName}>
                                    {yearName}-{(yearName + 1).toString().slice(2)}
                                 </option>
                              ))}
                           </select>
                        </div>
                     </>
                  );
               }
            })()}

            {(() => {
               // Default to 2024-25 format if survey year is empty or 2024
               const is2024Year = !formData.surveyYear || formData.surveyYear === 2024 || formData.surveyYear === '2024';

               return (
                  <>
                     <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                        <label htmlFor="pslNo"> {is2024Year ? '2.' : '3.'} {storedLanguage === 'hi' ? asiTitle.hi.PSL_NO : asiTitle.en.PSL_NO}</label>
                        <input
                           type="text"
                           className="form-control"
                           id="pslNo"
                           name="pslNo"
                           value={formData.pslNo}
                           onChange={handleChange}
                           disabled
                        />
                     </div>

                     <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                        <label htmlFor="schemeCode"> {is2024Year ? '3.' : '4.'} {storedLanguage === 'hi' ? asiTitle.hi.SCHEME_CODE : asiTitle.en.SCHEME_CODE}</label>
                        <input
                           type="text"
                           className="form-control"
                           id="schemeCode"
                           name="schemeCode"
                           value={formData.schemeCode}
                           onChange={handleChange}
                           disabled
                        />
                     </div>
                  </>
               );
            })()}

            {(() => {
               // Default to 2024-25 format if survey year is empty or 2024
               const is2024Year = !formData.surveyYear || formData.surveyYear === 2024 || formData.surveyYear === '2024';

               if (is2024Year) {
                  // For 2024-25, show new fields with new serial numbers
                  return (
                     <>
                        <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                           <label htmlFor="industryCodePerFrame"> 4. {storedLanguage === 'hi' ? asiTitle.hi.INDUSTRY_CODE_PER_FRAME : asiTitle.en.INDUSTRY_CODE_PER_FRAME}</label>
                           <input type="text"
                              className="form-control"
                              name="industryCodePerFrame"
                              id="industryCodePerFrame"
                              value={formData.industryCodePerFrame || formData.nicCodePerFrame}
                              onChange={handleChange}
                              disabled
                           />
                        </div>

                        <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                           <label htmlFor="industryCodePerReturn"> 5. {storedLanguage === 'hi' ? asiTitle.hi.INDUSTRY_CODE_PER_RETURN : asiTitle.en.INDUSTRY_CODE_PER_RETURN}</label>
                           <input
                              type="text"
                              className="form-control"
                              id="industryCodePerReturn"
                              name="industryCodePerReturn"
                              value={formData.industryCodePerReturn ?? ''}
                              onChange={handleChange}
                              maxLength="5"
                              placeholder='Enter 5 digits number'
                           />
                        </div>

                        <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                           <label htmlFor="descriptionOfIndustry"> 6. {storedLanguage === 'hi' ? asiTitle.hi.DESCRIPTION_OF_INDUSTRY : asiTitle.en.DESCRIPTION_OF_INDUSTRY}</label>
                           <input
                              type="text"
                              className="form-control"
                              id="descriptionOfIndustry"
                              name="descriptionOfIndustry"
                              value={formData.descriptionOfIndustry}
                              onChange={handleChange}
                              disabled
                           />
                        </div>

                        <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                           <label htmlFor="stateCode"> 7. {storedLanguage === 'hi' ? asiTitle.hi.STATE_CODE : asiTitle.en.STATE_CODE}</label>
                           <input
                              type="text"
                              className="form-control"
                              id="stateCode"
                              name="stateCode"
                              value={normalizeStateCode(formData.stateCode)}
                              onChange={handleChange}
                              disabled
                           />
                        </div>

                        <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                           <label htmlFor="districtCode"> 8. {storedLanguage === 'hi' ? asiTitle.hi.DISTRICT_CODE : asiTitle.en.DISTRICT_CODE}</label>
                           <input
                              type="text"
                              className="form-control"
                              id="districtCode"
                              name="districtCode"
                              value={formData.districtCode}
                              onChange={handleChange}
                              disabled
                           />
                        </div>

                        <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                           <label htmlFor="sector"> 9. {storedLanguage === 'hi' ? asiTitle.hi.SECTOR : asiTitle.en.SECTOR} (Rural-1, Urban-2)</label>
                           <input
                              type="text"
                              className="form-control"
                              id="sector"
                              name="sector"
                              value={formData.sector}
                              onChange={handleChange}
                              disabled
                           />
                        </div>

                        <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                           <label htmlFor="roSroCode"> 10. {storedLanguage === 'hi' ? asiTitle.hi.RO_SRO_CODE : asiTitle.en.RO_SRO_CODE}</label>
                           <input
                              type="text"
                              className="form-control"
                              id="roSroCode"
                              name="roSroCode"
                              value={formData.roSroCode}
                              onChange={handleChange}
                           />
                        </div>

                        <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                           <label htmlFor="numberOfUnits"> 11. {storedLanguage === 'hi' ? asiTitle.hi.NUMBER_OF_UNITS : asiTitle.en.NUMBER_OF_UNITS}</label>
                           <input
                              type="text"
                              className="form-control"
                              id="numberOfUnits"
                              name="numberOfUnits"
                              value={formData.numberOfUnits}
                              onChange={handleChange}
                              disabled
                           />
                        </div>

                        <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                           <label htmlFor="statusUnitCode" id="statusUnitCode-label">
                              12. {storedLanguage === 'hi' ? asiTitle.hi.STATUS_OF_UNIT_CODE : asiTitle.en.STATUS_OF_UNIT_CODE}
                           </label>
                           <StatusUnitDropdown
                              options={unitCodes}
                              value={formData.statusUnitCode}
                              onChange={handleChange}
                              id="statusUnitCode"
                              name="statusUnitCode"
                              placeholder="Select"
                              readOnly={previewMode}
                           />
                        </div>

                        {/* Reason For Data Entry - Hidden for 2024-25 */}
                        {decode_token.id.USER_TYPE !== 'FACTORY LEVEL' && !is2024Year && (
                           <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                              <label htmlFor="reason"> 13. {storedLanguage === 'hi' ? asiTitle.hi.REASON_FOR_DATA_ENTRY : asiTitle.en.REASON_FOR_DATA_ENTRY}</label>
                              <select
                                 className="form-select"
                                 id="reason"
                                 name="reason"
                                 value={formData.reason}
                                 onChange={handleChange}
                              >
                                 <option value="">Select</option>
                                 <option value="1">Factory is Denied for data entry</option>
                                 <option value="2">Factor is unable to data entry</option>
                                 <option value="3">Factory is asking help</option>
                              </select>
                           </div>
                        )}
                     </>
                  );
               } else {
                  // For other years, show old fields
                  return (
                     <>
                        <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                           <label htmlFor="nicCodePerFrame"> 5. {storedLanguage === 'hi' ? asiTitle.hi.NIC_CODE_PER_FRAME : asiTitle.en.NIC_CODE_PER_FRAME}</label>
                           <input type="text"
                              className="form-control"
                              name="nicCodePerFrame"
                              id="nicCodePerFrame"
                              value={formData.nicCodePerFrame || formData.industryCodePerFrame}
                              onChange={handleChange}
                              disabled
                           />
                        </div>

                        <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                           <label htmlFor="nicCodePerReturn"> 6. {storedLanguage === 'hi' ? asiTitle.hi.NIC_CODE_PER_RETURN : asiTitle.en.NIC_CODE_PER_RETURN}</label>
                           <input
                              type="text"
                              className="form-control"
                              id="nicCodePerReturn"
                              name="nicCodePerReturn"
                              value={formData.nicCodePerReturn ?? ''}
                              onChange={handleChange}
                              maxLength="5"
                              disabled
                           />
                        </div>

                        <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                           <label htmlFor="districtCode"> 7. {storedLanguage === 'hi' ? asiTitle.hi.DISTRICT_CODE : asiTitle.en.DISTRICT_CODE}</label>
                           <input
                              type="text"
                              className="form-control"
                              id="districtCode"
                              name="districtCode"
                              value={formData.districtCode}
                              onChange={handleChange}
                              disabled
                           />
                        </div>

                        <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                           <label htmlFor="sector"> 8. {storedLanguage === 'hi' ? asiTitle.hi.SECTOR : asiTitle.en.SECTOR}</label>
                           <input
                              type="text"
                              className="form-control"
                              id="sector"
                              name="sector"
                              value={formData.sector}
                              onChange={handleChange}
                              disabled
                           />
                        </div>

                        <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                           <label htmlFor="numberOfUnits"> 9. {storedLanguage === 'hi' ? asiTitle.hi.NUMBER_OF_UNITS : asiTitle.en.NUMBER_OF_UNITS}</label>
                           <input
                              type="text"
                              className="form-control"
                              id="numberOfUnits"
                              name="numberOfUnits"
                              value={formData.numberOfUnits}
                              onChange={handleChange}
                              disabled
                           />
                        </div>

                        <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                           <label htmlFor="statusUnitCode" id="statusUnitCode-label">
                              10. {storedLanguage === 'hi' ? asiTitle.hi.STATUS_OF_UNIT_CODE : asiTitle.en.STATUS_OF_UNIT_CODE}
                           </label>
                           <StatusUnitDropdown
                              options={unitCodes}
                              value={formData.statusUnitCode}
                              onChange={handleChange}
                              id="statusUnitCode"
                              name="statusUnitCode"
                              placeholder="Select"
                              readOnly={previewMode}
                           />
                        </div>

                        <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                           <label htmlFor="roSroCode"> 11. {storedLanguage === 'hi' ? asiTitle.hi.DESCRIPTION_OF_ALL_DSL_NO : asiTitle.en.DESCRIPTION_OF_ALL_DSL_NO}</label>
                           <input
                              type="text"
                              className="form-control"
                              id="roSroCode"
                              name="roSroCode"
                              value={formData.roSroCode}
                              onChange={handleChange}
                           />
                        </div>

                        {decode_token.id.USER_TYPE !== 'FACTORY LEVEL' && (
                           <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                              <label htmlFor="reason"> 12. {storedLanguage === 'hi' ? asiTitle.hi.REASON_FOR_DATA_ENTRY : asiTitle.en.REASON_FOR_DATA_ENTRY}</label>
                              <select
                                 className="form-select"
                                 id="reason"
                                 name="reason"
                                 value={formData.reason}
                                 onChange={handleChange}
                              >
                                 <option value="">Select</option>
                                 <option value="1">Factory is Denied for data entry</option>
                                 <option value="2">Factor is unable to data entry</option>
                                 <option value="3">Factory is asking help</option>
                              </select>
                           </div>
                        )}
                     </>
                  );
               }
            })()}



         </div>

         <div className="row">
            <div className="col-12">
               <hr />
            </div>

            <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">

               <button
                  type="submit"
                  className="btn btn_orange mx-1"
                  onClick={() => { window.print() }}
               >
                  Print
                  <i className="fa fa-print ms-1"></i>
               </button>

               <button
                  type="submit"
                  className="btn btn_orange mx-1"
                  onClick={(e) => { handleSaveData(e, "SAVE") }}
                  disabled={buttonStatus || isDivasiStatusFlagOne || isHqStatusFlagThree || isRestrictedRoleWithEmptyStatusFlag}>
                  Save
               </button>

               <button
                  type="reset"
                  className="btn btn_orange mx-1"
                  onClick={handleReset}
               >Reset
               </button>

               <button
                  type="button"
                  className="btn btn_orange mx-1"
                  onClick={(e) => (navigate("/dashboard"))}
               >
                  Cancel
               </button>
            </div>

            <div className="col-md-4 text-center text-md-end">
               <button
                  type="button"
                  className="btn btn_orange mx-1"
                  onClick={handleNext}
                  disabled={isNextBlockAlreadyAccessible ? false : (isRestrictedRoleWithEmptyStatusFlag ? true : ((isDivasiStatusFlagOne || isHqStatusFlagThree) ? false : buttonStatus))}
               >Next
                  <i className="fa fa-angle-right ms-1"></i>
               </button>
            </div>

         </div>
         {/* </form>           */}
      </div>
   )
}
